import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { UnifiedTable } from '../../../../components/UnifiedTable';
import { useApi } from '../../../../hooks/useApi';
import logger from '../../../../utils/logger';
import usePersistedState from '../../../../hooks/usePersistedState';

const FF_TABS = [
    { key: 'periods', label: 'Расчёт' },
    { key: 'costs', label: 'Стоимость товаров' },
    { key: 'ff', label: 'ФФ и склады' },
];

const MODE_OPTIONS = [
    { value: 'day', label: 'По дням' },
    { value: 'week', label: 'По неделям' },
    { value: 'month', label: 'По месяцам' },
];

const SHIPPING_MODE_OPTIONS = [
    { value: 'max', label: 'По самому дорогому товару' },
    { value: 'sum', label: 'Сумма по всем товарам' },
];

function defaultStartDate() {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
}

function money(v) {
    return (Number(v) || 0).toLocaleString('ru-RU', { maximumFractionDigits: 2 });
}

function OzonFbsFfCalculator({ accountId }) {
    const { request, authFetch, fetchBlob, API_BASE_URL, showStatus } = useApi();
    // Выбор пользователя должен переживать F5: начало периода — это дата начала работы
    // с ФФ, а оно сбрасывалось на "месяц назад" при каждом обновлении страницы.
    const prefix = `ozon-fbs-ffcalc-${accountId || 'none'}`;
    const [tab, setTab] = usePersistedState(`${prefix}-tab`, 'periods');
    const fileInputRef = useRef(null);

    // ФФ (фулфилмент-центры)
    const [ffList, setFfList] = useState([]);
    const [selectedFfId, setSelectedFfId] = usePersistedState(`${prefix}-ff-id`, '');
    const [loadingFf, setLoadingFf] = useState(false);
    const [warehouses, setWarehouses] = useState([]);
    const [newFfName, setNewFfName] = useState('');
    const [newFfWarehouses, setNewFfWarehouses] = useState([]);
    const [savingFf, setSavingFf] = useState(false);
    const [editingFfId, setEditingFfId] = useState('');
    const [editingFfWarehouses, setEditingFfWarehouses] = useState([]);
    const [editingFfName, setEditingFfName] = useState('');

    // Расчёт
    const [startDate, setStartDate] = usePersistedState(`${prefix}-start-date`, defaultStartDate());
    const [mode, setMode] = usePersistedState(`${prefix}-mode`, 'week');
    const [shippingMode, setShippingMode] = usePersistedState(`${prefix}-shipping-mode`, 'max');
    const [periods, setPeriods] = useState([]);
    const [loadingPeriods, setLoadingPeriods] = useState(false);
    const [syncing, setSyncing] = useState(false);

    // Стоимость товаров
    const [costRows, setCostRows] = useState([]);
    const [loadingCosts, setLoadingCosts] = useState(false);
    const [savingCosts, setSavingCosts] = useState(false);
    const [uploadingCosts, setUploadingCosts] = useState(false);

    const loadFfList = useCallback(async (keepSelection = true) => {
        if (!accountId) return;
        setLoadingFf(true);
        try {
            const [ffData, whData] = await Promise.all([
                request(`/api/v1/ozon-ops/${accountId}/fbs/ff-calc/ff`),
                request(`/api/v1/ozon-ops/${accountId}/fbs/ff-calc/warehouses`),
            ]);
            const items = Array.isArray(ffData?.items) ? ffData.items : [];
            setFfList(items);
            setWarehouses(Array.isArray(whData?.items) ? whData.items : []);
            setSelectedFfId(prev => {
                if (keepSelection && prev && items.some(f => f.id === prev)) return prev;
                return items[0]?.id || '';
            });
        } catch (e) {
            logger.error(e);
            showStatus('error', e?.message || 'Ошибка загрузки списка ФФ.');
        } finally {
            setLoadingFf(false);
        }
    }, [accountId, request, showStatus, setSelectedFfId]);

    useEffect(() => { loadFfList(true); }, [loadFfList]);

    const loadPeriods = useCallback(async () => {
        if (!accountId || !startDate || !selectedFfId) return;
        setLoadingPeriods(true);
        try {
            const data = await request(
                `/api/v1/ozon-ops/${accountId}/fbs/ff-calc/periods?ff_id=${selectedFfId}&start_date=${startDate}&mode=${mode}&shipping_cost_mode=${shippingMode}`
            );
            setPeriods(Array.isArray(data?.items) ? data.items : []);
        } catch (e) {
            logger.error(e);
            showStatus('error', e?.message || 'Ошибка расчёта периодов.');
        } finally {
            setLoadingPeriods(false);
        }
    }, [accountId, startDate, mode, shippingMode, selectedFfId, request, showStatus]);

    const loadCosts = useCallback(async () => {
        if (!accountId || !selectedFfId) return;
        setLoadingCosts(true);
        try {
            const loadAllAssortment = async () => {
                const items = [];
                for (let page = 1; ; page += 1) {
                    const data = await request(`/api/v1/ozon-ops/${accountId}/assortment?page=${page}&page_size=500`);
                    const chunk = data?.items || [];
                    items.push(...chunk);
                    if (!chunk.length || items.length >= Number(data?.total || 0)) break;
                }
                return { items };
            };
            const [assortmentData, costsData] = await Promise.all([
                loadAllAssortment(),
                request(`/api/v1/ozon-ops/${accountId}/fbs/ff-calc/costs?ff_id=${selectedFfId}`),
            ]);
            const costMap = {};
            (costsData?.items || []).forEach(c => { costMap[c.offer_id] = c; });
            const rows = (assortmentData?.items || []).map(a => ({
                offer_id: a.offer_id,
                name: a.name,
                sku: a.sku,
                packaging_cost: costMap[a.offer_id]?.packaging_cost ?? '',
                shipping_cost: costMap[a.offer_id]?.shipping_cost ?? '',
            }));
            setCostRows(rows);
        } catch (e) {
            logger.error(e);
            showStatus('error', e?.message || 'Ошибка загрузки стоимости.');
        } finally {
            setLoadingCosts(false);
        }
    }, [accountId, selectedFfId, request, showStatus]);

    useEffect(() => {
        if (!selectedFfId) return;
        if (tab === 'periods') loadPeriods();
        if (tab === 'costs') loadCosts();
        // Пересчёт — чистый локальный запрос (без обращения к Ozon), поэтому дергаем его
        // автоматически при смене даты/режима, а не только по кнопке «Пересчитать»: иначе
        // в таблице виден расчёт для уже неактуального периода до явного нажатия кнопки.
    }, [tab, accountId, selectedFfId, startDate, mode, shippingMode, loadPeriods, loadCosts]);

    const handleSyncAndCalc = async () => {
        setSyncing(true);
        try {
            await request(`/api/v1/ozon-ops/${accountId}/fbs/postings/sync`, { method: 'POST' });
            await loadPeriods();
            showStatus('success', 'Заказы обновлены, расчёт пересчитан.');
        } catch (e) {
            logger.error(e);
            showStatus('error', e?.message || 'Ошибка синхронизации.');
        } finally {
            setSyncing(false);
        }
    };

    const togglePaid = async (period, isPaid) => {
        setPeriods(prev => prev.map(p => p === period ? { ...p, is_paid: isPaid } : p));
        try {
            await request(`/api/v1/ozon-ops/${accountId}/fbs/ff-calc/periods/paid`, {
                method: 'POST',
                body: JSON.stringify({
                    ff_id: selectedFfId,
                    mode,
                    period_start: period.period_start,
                    period_end: period.period_end,
                    is_paid: isPaid,
                }),
            });
        } catch (e) {
            logger.error(e);
            showStatus('error', e?.message || 'Не удалось сохранить отметку «Оплачено».');
            setPeriods(prev => prev.map(p => p === period ? { ...p, is_paid: !isPaid } : p));
        }
    };

    const updateCostField = (offerId, field, value) => {
        setCostRows(prev => prev.map(r => r.offer_id === offerId ? { ...r, [field]: value } : r));
    };

    const handleSaveCosts = async () => {
        setSavingCosts(true);
        try {
            const items = costRows
                .filter(r => r.packaging_cost !== '' || r.shipping_cost !== '')
                .map(r => ({
                    offer_id: r.offer_id,
                    packaging_cost: r.packaging_cost === '' ? null : Number(r.packaging_cost),
                    shipping_cost: r.shipping_cost === '' ? null : Number(r.shipping_cost),
                }));
            const res = await request(`/api/v1/ozon-ops/${accountId}/fbs/ff-calc/costs?ff_id=${selectedFfId}`, {
                method: 'PUT',
                body: JSON.stringify({ items }),
            });
            showStatus('success', `Сохранено: ${res?.saved ?? 0} товаров.`);
        } catch (e) {
            logger.error(e);
            showStatus('error', e?.message || 'Ошибка сохранения стоимости.');
        } finally {
            setSavingCosts(false);
        }
    };

    const handleDownloadTemplate = async () => {
        try {
            const blob = await fetchBlob(`/api/v1/ozon-ops/${accountId}/fbs/ff-calc/costs/template?ff_id=${selectedFfId}`);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'ff_costs_template.xlsx';
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (e) {
            logger.error(e);
            showStatus('error', 'Ошибка скачивания шаблона.');
        }
    };

    const handleUploadFile = async (fileObj) => {
        if (!fileObj) return;
        setUploadingCosts(true);
        try {
            const formData = new FormData();
            formData.append('file', fileObj);
            const res = await authFetch(`${API_BASE_URL}/api/v1/ozon-ops/${accountId}/fbs/ff-calc/costs/upload?ff_id=${selectedFfId}`, {
                method: 'POST',
                body: formData,
            });
            showStatus('success', `Загружено из файла: ${res?.saved ?? 0} товаров.`);
            await loadCosts();
        } catch (e) {
            logger.error(e);
            showStatus('error', e?.message || 'Ошибка загрузки файла.');
        } finally {
            setUploadingCosts(false);
        }
    };

    const handleCreateFf = async () => {
        if (!newFfName.trim()) {
            showStatus('error', 'Укажите название ФФ.');
            return;
        }
        setSavingFf(true);
        try {
            await request(`/api/v1/ozon-ops/${accountId}/fbs/ff-calc/ff`, {
                method: 'POST',
                body: JSON.stringify({ name: newFfName.trim(), warehouse_ids: newFfWarehouses }),
            });
            setNewFfName('');
            setNewFfWarehouses([]);
            await loadFfList();
            showStatus('success', 'ФФ создан.');
        } catch (e) {
            logger.error(e);
            showStatus('error', e?.message || 'Ошибка создания ФФ.');
        } finally {
            setSavingFf(false);
        }
    };

    const startEditFf = (ff) => {
        setEditingFfId(ff.id);
        setEditingFfName(ff.name);
        setEditingFfWarehouses(ff.warehouse_ids || []);
    };

    const handleSaveFfEdit = async () => {
        setSavingFf(true);
        try {
            await request(`/api/v1/ozon-ops/${accountId}/fbs/ff-calc/ff/${editingFfId}`, {
                method: 'PATCH',
                body: JSON.stringify({ name: editingFfName.trim(), warehouse_ids: editingFfWarehouses }),
            });
            setEditingFfId('');
            await loadFfList();
            showStatus('success', 'ФФ обновлён.');
        } catch (e) {
            logger.error(e);
            showStatus('error', e?.message || 'Ошибка сохранения ФФ.');
        } finally {
            setSavingFf(false);
        }
    };

    const handleDeleteFf = async (ff) => {
        setSavingFf(true);
        try {
            await request(`/api/v1/ozon-ops/${accountId}/fbs/ff-calc/ff/${ff.id}`, { method: 'DELETE' });
            await loadFfList(false);
            showStatus('success', `ФФ «${ff.name}» удалён.`);
        } catch (e) {
            logger.error(e);
            showStatus('error', e?.message || 'Ошибка удаления ФФ.');
        } finally {
            setSavingFf(false);
        }
    };

    // Склад может принадлежать только одному ФФ — иначе одна и та же отгрузка задвоится в расчёте.
    const warehouseOwner = useMemo(() => {
        const map = {};
        ffList.forEach(f => {
            (f.warehouse_ids || []).forEach(wid => { map[wid] = f; });
        });
        return map;
    }, [ffList]);

    const toggleWarehouse = (list, setList, warehouseId) => {
        setList(list.includes(warehouseId) ? list.filter(w => w !== warehouseId) : [...list, warehouseId]);
    };

    const periodColumns = useMemo(() => [
        { key: 'label', header: 'Период', width: 200, type: 'text', sticky: true, sortable: false },
        { key: 'postings_count', header: 'Отправлений', width: 130, type: 'number', sortable: false },
        { key: 'items_count', header: 'Товаров', width: 110, type: 'number', sortable: false },
        { key: 'packaging_total', header: 'Упаковка', width: 120, type: 'text', sortable: false, render: (v) => money(v) },
        { key: 'shipping_total', header: 'Отгрузка', width: 120, type: 'text', sortable: false, render: (v) => money(v) },
        { key: 'total_ff', header: 'Итого ФФ', width: 130, type: 'text', sortable: false, render: (v) => <strong>{money(v)}</strong> },
        {
            key: 'is_paid', header: 'Оплачено', width: 100, type: 'custom', sortable: false, resizable: false,
            render: (v, row) => (
                <input type="checkbox" checked={!!v} onChange={e => togglePaid(row, e.target.checked)} />
            ),
        },
        // eslint-disable-next-line
    ], [mode, selectedFfId]);

    const costColumns = useMemo(() => [
        { key: 'offer_id', header: 'Артикул', width: 180, type: 'text', sticky: true, sortable: false },
        { key: 'sku', header: 'SKU', width: 140, type: 'text', sortable: false },
        { key: 'name', header: 'Название', width: 320, type: 'text', sortable: false },
        {
            key: 'packaging_cost', header: 'Стоимость упаковки', width: 170, type: 'custom', sortable: false, resizable: false,
            render: (v, row) => (
                <input
                    type="number" step="0.01" style={{ width: '100%' }}
                    value={v}
                    onChange={e => updateCostField(row.offer_id, 'packaging_cost', e.target.value)}
                />
            ),
        },
        {
            key: 'shipping_cost', header: 'Стоимость отгрузки', width: 170, type: 'custom', sortable: false, resizable: false,
            render: (v, row) => (
                <input
                    type="number" step="0.01" style={{ width: '100%' }}
                    value={v}
                    onChange={e => updateCostField(row.offer_id, 'shipping_cost', e.target.value)}
                />
            ),
        },
        // eslint-disable-next-line
    ], []);

    const periodsToolbarActions = useMemo(() => ([
        {
            label: syncing ? 'Синхронизация…' : 'Обновить заказы и расчёт',
            onClick: handleSyncAndCalc,
            disabled: syncing,
        },
        {
            label: loadingPeriods ? 'Расчёт…' : 'Пересчитать',
            onClick: loadPeriods,
            disabled: loadingPeriods,
        },
        // eslint-disable-next-line
    ]), [syncing, loadingPeriods]);

    const costsToolbarActions = useMemo(() => ([
        { label: 'Скачать шаблон', onClick: handleDownloadTemplate },
        { label: uploadingCosts ? 'Загрузка…' : 'Загрузить из файла', onClick: () => fileInputRef.current?.click(), disabled: uploadingCosts },
        { label: savingCosts ? 'Сохранение…' : 'Сохранить', onClick: handleSaveCosts, disabled: savingCosts },
        // eslint-disable-next-line
    ]), [uploadingCosts, savingCosts, costRows, selectedFfId]);

    const ffSelector = (
        <label>
            ФФ (фулфилмент-центр)
            <select value={selectedFfId} onChange={e => setSelectedFfId(e.target.value)} disabled={loadingFf || ffList.length === 0}>
                {ffList.length === 0 && <option value="">Нет ни одного ФФ</option>}
                {ffList.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
        </label>
    );

    return (
        <div className="stack-12">
            <div className="account-tabs account-tabs--secondary" style={{ marginBottom: 0 }}>
                {FF_TABS.map(t => (
                    <button key={t.key} type="button"
                        className={tab === t.key ? 'active' : ''}
                        onClick={() => setTab(t.key)}>
                        {t.label}
                    </button>
                ))}
            </div>

            {tab !== 'ff' && ffList.length === 0 && !loadingFf && (
                <div className="panel__section">
                    <p className="muted">
                        Ещё не создан ни один ФФ (фулфилмент-центр). Расчёт ведётся по складам, привязанным к ФФ —
                        сначала создайте хотя бы один на вкладке «ФФ и склады».
                    </p>
                </div>
            )}

            {tab === 'periods' && ffList.length > 0 && (
                <div className="panel__section">
                    <div className="panel__grid ozon-fbs-ff-controls">
                        {ffSelector}
                        <label>
                            Начало периода
                            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
                        </label>
                        <label>
                            Режим
                            <select value={mode} onChange={e => setMode(e.target.value)}>
                                {MODE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                        </label>
                        <label>
                            Стоимость отгрузки при нескольких товарах
                            <select value={shippingMode} onChange={e => setShippingMode(e.target.value)}>
                                {SHIPPING_MODE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                        </label>
                    </div>

                    <UnifiedTable
                        tableId="ozon-fbs-ff-periods"
                        columns={periodColumns}
                        data={periods}
                        totalRows={periods.length}
                        loading={loadingPeriods}
                        toolbarActions={periodsToolbarActions}
                        onExportCsv={true}
                        onCopyTable={true}
                        getRowKey={(row) => row.label}
                    />
                </div>
            )}

            {tab === 'costs' && ffList.length > 0 && (
                <div className="panel__section">
                    <div className="panel__grid ozon-fbs-ff-controls">{ffSelector}</div>
                    <p className="muted" style={{ marginTop: 0 }}>
                        Стоимость упаковки и отгрузки — за единицу товара, своя для выбранного ФФ. Пусто = не учитывается в расчёте.
                        Можно редактировать прямо в таблице или заполнить через шаблон Excel.
                    </p>
                    <input
                        ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }}
                        onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) handleUploadFile(f); }}
                    />
                    <UnifiedTable
                        tableId="ozon-fbs-ff-costs"
                        columns={costColumns}
                        data={costRows}
                        totalRows={costRows.length}
                        loading={loadingCosts}
                        toolbarActions={costsToolbarActions}
                        onExportCsv={true}
                        onCopyTable={true}
                        getRowKey={(row) => row.offer_id}
                    />
                </div>
            )}

            {tab === 'ff' && (
                <div className="panel__section">
                    <h4 style={{ marginTop: 0 }}>Новый ФФ</h4>
                    <div className="panel__grid">
                        <label>
                            Название
                            <input type="text" value={newFfName} onChange={e => setNewFfName(e.target.value)} placeholder="например, ФФ Москва" />
                        </label>
                    </div>
                    <p className="muted" style={{ marginBottom: 6 }}>
                        Склады этого ФФ (один склад — только в одном ФФ, иначе отгрузка задвоится в расчёте):
                    </p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                        {warehouses.length === 0 && <span className="muted">Нет данных о складах — синхронизируйте заказы хотя бы раз.</span>}
                        {warehouses.map(w => {
                            const owner = warehouseOwner[w.warehouse_id];
                            const taken = !!owner;
                            return (
                                <label key={w.warehouse_id}
                                    title={taken ? `Уже привязан к ФФ «${owner.name}»` : undefined}
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: 4, fontSize: 13,
                                        border: '1px solid var(--border, #e5e7eb)', borderRadius: 6, padding: '4px 8px',
                                        opacity: taken ? 0.5 : 1,
                                    }}>
                                    <input
                                        type="checkbox"
                                        checked={newFfWarehouses.includes(w.warehouse_id)}
                                        disabled={taken}
                                        onChange={() => toggleWarehouse(newFfWarehouses, setNewFfWarehouses, w.warehouse_id)}
                                    />
                                    {w.name}{taken ? ` (занят: ${owner.name})` : ''}
                                </label>
                            );
                        })}
                    </div>
                    <button type="button" className="btn-primary" onClick={handleCreateFf} disabled={savingFf}>
                        {savingFf ? 'Сохранение…' : 'Создать ФФ'}
                    </button>

                    <h4 style={{ marginTop: 24 }}>Существующие ФФ</h4>
                    {ffList.length === 0 && <p className="muted">Пока ни одного ФФ не создано.</p>}
                    {ffList.map(ff => (
                        <div key={ff.id} className="panel__section" style={{ marginTop: 8, background: 'var(--glass-90)' }}>
                            {editingFfId === ff.id ? (
                                <>
                                    <div className="panel__grid">
                                        <label>
                                            Название
                                            <input type="text" value={editingFfName} onChange={e => setEditingFfName(e.target.value)} />
                                        </label>
                                    </div>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, margin: '8px 0' }}>
                                        {warehouses.map(w => {
                                            const owner = warehouseOwner[w.warehouse_id];
                                            const taken = !!owner && owner.id !== ff.id;
                                            return (
                                                <label key={w.warehouse_id}
                                                    title={taken ? `Уже привязан к ФФ «${owner.name}»` : undefined}
                                                    style={{
                                                        display: 'flex', alignItems: 'center', gap: 4, fontSize: 13,
                                                        border: '1px solid var(--surface-border)', borderRadius: 6, padding: '4px 8px',
                                                        opacity: taken ? 0.5 : 1,
                                                    }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={editingFfWarehouses.includes(w.warehouse_id)}
                                                        disabled={taken}
                                                        onChange={() => toggleWarehouse(editingFfWarehouses, setEditingFfWarehouses, w.warehouse_id)}
                                                    />
                                                    {w.name}{taken ? ` (занят: ${owner.name})` : ''}
                                                </label>
                                            );
                                        })}
                                    </div>
                                    <div style={{ display: 'flex', gap: 8 }}>
                                        <button type="button" className="btn-primary" onClick={handleSaveFfEdit} disabled={savingFf}>Сохранить</button>
                                        <button type="button" onClick={() => setEditingFfId('')}>Отмена</button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <strong>{ff.name}</strong>
                                        <div style={{ display: 'flex', gap: 8 }}>
                                            <button type="button" onClick={() => startEditFf(ff)}>Редактировать</button>
                                            <button type="button" onClick={() => handleDeleteFf(ff)} disabled={savingFf}>Удалить</button>
                                        </div>
                                    </div>
                                    <p className="muted" style={{ margin: '6px 0 0' }}>
                                        Склады: {ff.warehouses?.length ? ff.warehouses.map(w => w.name).join(', ') : '—'}
                                    </p>
                                </>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default OzonFbsFfCalculator;
